import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, Subscription, take } from 'rxjs';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { GlaiService } from '../../../services/glai/glai.service';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import {
  ProformFieldTypes,
  ProFormsQuestionAPIResponse,
} from '../../../types/responses/proforms.responses';
import { CkEditorComponent } from '../../ck-editor/ck-editor.component';

@Component({
  selector: 'app-proforms-question-answer-text',
  standalone: false,
  templateUrl: './proforms-question-answer-text.component.html',
  styleUrl: './proforms-question-answer-text.component.scss',
})
export class ProformsQuestionAnswerTextComponent implements OnInit, OnDestroy {
  @ViewChild('contentEditor') contentEditor: CkEditorComponent;

  @Input() question: ProFormsQuestionAPIResponse;

  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @Input() isObservationSession = false;

  @Input() formId: number;

  FieldTypes = ProformFieldTypes;

  viewStates = ProformViewStates;

  textControl: FormControl;

  textareaControl: FormControl;

  debounceTime = 500;

  subscriptions = new Subscription();

  hasUserEdited = false;

  initialTextareaValue: string | null = null;

  smartResponsePreview: string | null = null;

  smartResponseLoading = false;

  smartResponseError: string | null = null;

  smartResponseId: number | null = null;

  smartResponseUserFeedback: string | null = null;

  constructor(
    private proformsSubmissionService: ProformsSubmissionService,
    private glaiService: GlaiService
  ) {}

  ngOnInit(): void {
    this.textControl = new FormControl({
      value: '',
      disabled:
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED,
    });

    this.textareaControl = new FormControl({
      value: '',
      disabled:
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED,
    });

    if (this.question) {
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.subscriptions.add(
          this.proformsSubmissionService.currentSubmission.subscribe(
            (submission) => {
              const answer = submission?.answers?.find(
                (a) => a.proform_form_question_id === this.question.id
              );
              this.textareaControl.setValue(answer?.text_answer ?? '', {
                emitEvent: false,
              });
            }
          )
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        // Hydrate from the editable payload stream. This avoids races where the
        // payload is created after the field component initializes.
        this.subscriptions.add(
          this.proformsSubmissionService.newSubmission.subscribe(
            (submission) => {
              if (submission === null) return;
              const answer = submission.answers?.find(
                (a) => a.proform_form_question_id === this.question.id
              );
              const value = answer?.text_answer ?? '';
              if (this.initialTextareaValue === null) {
                // First real emission — set initialData before editor mounts
                this.initialTextareaValue = value;
              } else if (!this.hasUserEdited) {
                // Late emission after editor is live
                this.contentEditor?.setEditorContent(value);
              }
            }
          )
        );

        // Listeners for updating payload values
        if (this.question.field_type === this.FieldTypes.TEXT) {
          this.subscriptions.add(
            this.textControl.valueChanges
              .pipe(debounceTime(this.debounceTime))
              .subscribe((value) => {
                this.hasUserEdited = true;
                this.proformsSubmissionService.addAnswerToPayload(
                  {
                    proform_form_question_id: this.question.id,
                    text_answer: value,
                  },
                  this.question.field_type
                );
              })
          );
        } else {
          this.subscriptions.add(
            this.textareaControl.valueChanges
              .pipe(debounceTime(this.debounceTime))
              .subscribe((value) => {
                this.hasUserEdited = true;
                this.smartResponsePreview = null;
                this.proformsSubmissionService.addAnswerToPayload(
                  {
                    proform_form_question_id: this.question.id,
                    text_answer: value,
                  },
                  this.question.field_type
                );
              })
          );
        }
      }
    }

    // PREVIEW has no async data — editor can render immediately
    if (this.viewState === this.viewStates.PREVIEW) {
      this.initialTextareaValue = '';
    }
  }

  generateSmartResponse(): void {
    this.smartResponseLoading = true;
    this.smartResponsePreview = null;
    this.smartResponseError = null;

    this.proformsSubmissionService.newSubmission
      .pipe(take(1))
      .subscribe((submission) => {
        this.glaiService
          .generateProformSmartResponse({
            question_id: this.question.id,
            form_id: this.formId,
            answers: submission?.answers ?? [],
          })
          .subscribe({
            next: (result) => {
              this.smartResponsePreview = result.response;
              this.smartResponseId = result.glai_content_id;
              this.smartResponseUserFeedback = null;
              this.smartResponseLoading = false;
            },
            error: (error) => {
              this.smartResponseLoading = false;
              this.smartResponseError =
                'There was an error generating a smart response. Please try again.';
              // eslint-disable-next-line no-console
              console.error(error);
            },
          });
      });
  }

  acceptSmartResponse(): void {
    if (this.smartResponsePreview) {
      this.contentEditor?.setEditorContent(this.smartResponsePreview);
      this.smartResponsePreview = null;
    }
  }

  dismissSmartResponse(): void {
    this.smartResponsePreview = null;
  }

  submitSmartResponseFeedback(direction: string): void {
    if (!this.smartResponseId) return;
    this.smartResponseUserFeedback = direction;
    this.glaiService
      .submitGlaiContentFeedback(this.smartResponseId, direction)
      .subscribe();
  }

  updateQuestionAnswerText(value: string): void {
    if (this.question.field_type === this.FieldTypes.TEXTAREA) {
      this.textareaControl.setValue(value, { emitEvent: true });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
